/* -------------------------------------------------------------------------
   TransactionSearchResultModal.jsx — draggable + filters + sorting + MUI lists
--------------------------------------------------------------------------- */
import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import Draggable from "react-draggable";
import {
    Dialog, DialogTitle, DialogContent, IconButton, Typography,
    Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TextField, Button, Divider, TableSortLabel, Box,
    ToggleButton, ToggleButtonGroup
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";


import DocumentService from "../../../Services/documents.service";
import { calculateVatAmount } from "../../../Utils/GetVatValue";
import { beautifyNumber } from "../../../Utils/FormatNumber";
import { buildPaymentAcceptanceUpdatePayload } from "../../../Utils/paymentAcceptanceUpdatePayload";
import NoDocumentsPdf from "../../../assests/pdf/NoDocument.pdf";

import EditModal from "../../EditModal/EditModal";
import Constants from "../../../assests/Constants/constants";
import PaymentAcceptanceCreateEditModal from "../../PaymentAcceptance/SubComponents/PaymentAcceptanceCreateEditModal";
import SubAccountantService from "../../../Services/subAccountant.service";
import ClientService from "../../../Services/client.service";
import { validateDocumentBeforeProcessing } from "../../../Utils/documentValidation";
import { canProcessDocumentStatus } from "../../../Utils/helpers";
import CustomDeleteConfirmationModal from "../../CustomDeleteConfirmationModal/CustomDeleteConfirmationModal";
import ClientDocumentCountService from "../../../Services/ClientDocumentCount.service";
import { toast } from "react-toastify";
import EnumsService from "../../../Services/enums.service";
import { validatePaymentAcceptanceItems } from "../../../Utils/documentValidation";
import { useHamburgerMenu } from "../../../Contexts/HamburgerMenuContext";
import { PermissionsContext } from "../../../Contexts/Permissions";
import AutocompleteSelectFilter from "../AutocompleteSelectFilter/AutocompleteSelectFilter";

import "./TransactionSearchResultModal.css";
dayjs.extend(utc);


/* ───────────────── helper ───────────────── */
const PaperDraggable = (props) => (
    <Draggable handle="#dialog-draggable-title" cancel={'[class*="MuiDialogContent-root"]'}>
        <Paper {...props} />
    </Draggable>
);

/** Selectable filter criteria — only selected keys render in the search bar */
const DEFAULT_ACTIVE_FILTERS = [
    "documentDate",
    "amount",
    "sortCode",
    "debitAccount",
    "creditAccount",
    "description",
    "reference",
];

const FILTER_PREFS_STORAGE_KEY = "txnSearch.activeFilters";
const VALID_FILTER_KEYS = new Set([
    "documentDate",
    "uploadDate",
    "reportingMonth",
    "amount",
    "sortCode",
    "debitAccount",
    "creditAccount",
    "description",
    "reference",
    "reference2",
    "accountCardRange",
    "confirmationNumber",
    "uploadNumber",
    "accountCaseId",
]);

const loadSavedFilterPrefs = () => {
    try {
        const raw = localStorage.getItem(FILTER_PREFS_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const keys = Array.isArray(parsed?.activeFilterKeys)
            ? parsed.activeFilterKeys.filter((k) => VALID_FILTER_KEYS.has(k))
            : null;
        const referenceMode = ["exact", "range", "include"].includes(parsed?.referenceMode)
            ? parsed.referenceMode
            : null;
        return {
            activeFilterKeys: keys?.length ? keys : null,
            referenceMode,
        };
    } catch {
        return null;
    }
};

const saveFilterPrefs = (activeFilterKeys, referenceMode) => {
    try {
        localStorage.setItem(
            FILTER_PREFS_STORAGE_KEY,
            JSON.stringify({ activeFilterKeys, referenceMode })
        );
    } catch {
        /* ignore quota / private mode */
    }
};

const FILTER_FIELD_RESETS = {
    documentDate: { fromDate: null, toDate: null },
    uploadDate: { uploadDateFrom: null, uploadDateTo: null },
    reportingMonth: { reportingMonthFrom: null, reportingMonthTo: null },
    amount: { minAmount: "", maxAmount: "" },
    sortCode: { sortCodeId: "" },
    debitAccount: { debitAccount: "" },
    creditAccount: { creditAccount: "" },
    description: { descriptionContains: "" },
    reference: { referenceContains: "", referenceFrom: "", referenceTo: "", referenceMode: "exact" },
    reference2: { reference2Contains: "" },
    accountCardRange: { accountCardFrom: "", accountCardTo: "" },
    confirmationNumber: { confirmationNumber: "" },
    uploadNumber: { uploadNumberFrom: "", uploadNumberTo: "" },
    accountCaseId: { accountCaseId: "" },
};

const FilterGroup = ({ label, wide = false, compact = false, children }) => (
    <Box
        className={`txn-search-filter-group${wide ? " txn-search-filter-group--wide" : ""}${compact ? " txn-search-filter-group--compact" : ""}`}
        sx={{ textAlign: "start" }}
    >
        {label ? (
            <div className="txn-search-filter-group-label">
                {label}
            </div>
        ) : null}
        <Box className="txn-search-filter-group-fields">{children}</Box>
    </Box>
);

FilterGroup.propTypes = {
    label: PropTypes.node,
    wide: PropTypes.bool,
    compact: PropTypes.bool,
    children: PropTypes.node,
};

/* ───────────────── Data-table component ───────────────── */
const TxnTable = ({ rows, onRowClick }) => {
    const { t } = useTranslation();

    /* tiny header filters + sort */
    const [filters, setFilters] = useState({
        uploadNumber: "", debit: "", credit: "", date: "",
        desc: "", ref: "", ref2: "", sort: ""
    });
    const [sort, setSort] = useState({ field: "uploadNumber", dir: "asc" });

    const filteredRows = useMemo(() => {
        const like = (v, f) => (f ? String(v ?? "").toLowerCase().includes(f.toLowerCase()) : true);

        return [...rows]
            .filter(r =>
                like(r.uploadNumber, filters.uploadNumber) &&
                like(r.debitAccountName || r.debitAccount, filters.debit) &&
                like(r.creditAccountName || r.creditAccount, filters.credit) &&
                like(r.documentDate, filters.date) &&
                like(r.description, filters.desc) &&
                like(r.reference, filters.ref) &&
                like(r.reference2, filters.ref2) &&
                like(r.sortCodeName || r.sortCodeId, filters.sort))
            .sort((a, b) => {
                const { field, dir } = sort;
                const av = a[field] ?? "";
                const bv = b[field] ?? "";
                if (av === bv) return 0;
                const cmp = av > bv ? 1 : -1;
                return dir === "asc" ? cmp : -cmp;
            });
    }, [rows, filters, sort]);

    const toggleSort = (field) =>
        setSort(prev => ({
            field,
            dir: field === prev.field && prev.dir === "asc" ? "desc" : "asc",
        }));

    if (!rows?.length)
        return <Typography className="txn-search-empty">{t("NoData")}</Typography>;

    return (
        <TableContainer component={Paper} elevation={0} className="txn-search-table-wrap">
            <Table size="small">
                <TableHead>
                    <TableRow className="bg-primary-50 dark:bg-primary-900">
                        {[
                            ["uploadNumber", t("TransactionNumber"), "center", "txn-col-upload"],
                            ["debit", t("Debit Account"), "center", "txn-col-account"],
                            ["credit", t("Credit Account"), "center", "txn-col-account"],
                            ["date", t("Date"), "center", "txn-col-date"],
                            ["desc", t("Description"), "center", "txn-col-desc"],
                            ["ref", t("Reference"), "center", "txn-col-ref"],
                            ["ref2", t("Reference2"), "center", "txn-col-ref"],
                            ["amount", t("Amount"), "right", "txn-col-num"],
                            ["net", t("NetAmount"), "right", "txn-col-num"],
                            ["vat", t("VATAmount"), "right", "txn-col-num"],
                            ["month", t("MonthReportDate"), "center", "txn-col-month"],
                            ["sort", t("SortCode"), "center", "txn-col-sort"],
                        ].map(([key, label, align, cellClass]) => (
                            <TableCell key={key} align={align} className={cellClass}>
                                <TableSortLabel
                                    active={sort.field === key}
                                    direction={sort.field === key ? sort.dir : "asc"}
                                    onClick={() => toggleSort(key)}
                                >
                                    {label}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                    </TableRow>
                    {/* filter row */}
                    <TableRow className="filter-row">
                        {[
                            "uploadNumber", "debit", "credit", "date",
                            "desc", "ref", "ref2", "", "", "", "month", "sort",
                        ].map((f, idx) => (
                            <TableCell
                                key={idx}
                                align={idx >= 7 && idx <= 9 ? "right" : "center"}
                                className={[
                                    "txn-col-upload",
                                    "txn-col-account",
                                    "txn-col-account",
                                    "txn-col-date",
                                    "txn-col-desc",
                                    "txn-col-ref",
                                    "txn-col-ref",
                                    "txn-col-num",
                                    "txn-col-num",
                                    "txn-col-num",
                                    "txn-col-month",
                                    "txn-col-sort",
                                ][idx]}
                            >
                                {f && (
                                    <input
                                        className="filter-input"
                                        value={filters[f]}
                                        onChange={(e) => setFilters({ ...filters, [f]: e.target.value })}
                                    />
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {filteredRows.map((r) => {
                        const vat = calculateVatAmount(r.amount, r.vat, r.documentDate);
                        const net = r.amount - vat;
                        const cls = [
                            r.isUnprocessed ? "missing-row" : "",
                            r.isDetail ? "detail-row" : "",
                        ].join(" ");

                        return (
                            <TableRow
                                key={r.id}
                                hover
                                className={cls}
                                sx={{ cursor: onRowClick ? "pointer" : "default" }}
                                onClick={() => onRowClick?.(r)}
                            >
                                <TableCell align="center" className="txn-col-upload">{r.isDetail ? "↳" : ""} {r.uploadNumber ?? "—"}</TableCell>
                                <TableCell align="center" className="txn-col-account">{r.debitAccountName || r.debitAccount || "—"}</TableCell>
                                <TableCell align="center" className="txn-col-account">{r.creditAccountName || r.creditAccount || "—"}</TableCell>
                                <TableCell align="center" className="txn-col-date">{String(r.documentDate).substring(0, 10)}</TableCell>
                                <TableCell align="center" className="txn-col-desc">{r.description || "—"}</TableCell>
                                <TableCell align="center" className="txn-col-ref">{r.reference || "—"}</TableCell>
                                <TableCell align="center" className="txn-col-ref">{r.reference2 || "—"}</TableCell>
                                <TableCell align="right" className="txn-col-num">{beautifyNumber(r.amount)}</TableCell>
                                <TableCell align="right" className="txn-col-num">{beautifyNumber(net)}</TableCell>
                                <TableCell align="right" className="txn-col-num">{beautifyNumber(vat)}</TableCell>
                                <TableCell align="center" className="txn-col-month">
                                    {r.reportingMonth
                                        ? new Date(r.reportingMonth).toLocaleDateString("he-IL", { year: "numeric", month: "2-digit" })
                                        : "—"}
                                </TableCell>
                                <TableCell align="center" className="txn-col-sort">{r.sortCodeName || r.sortCodeId || "—"}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

TxnTable.propTypes = { rows: PropTypes.array.isRequired, onRowClick: PropTypes.func };

/* ────────────────────────────── MAIN MODAL ────────────────────────────── */
const TransactionSearchResultModal = ({
    open, onClose,
    caseId, accountantId, token,
    accountsData = [],
    sortCodes = [],
    client,
    user,
    onRowClick,
    onSelectDocument,
    dialogZIndex,
    // New props for direct edit modal
    openJustEditModal = false,
    selectedDocumentId = null,
    selectedSectionKey = null,
    editModalDataSource = null,
    setEditModalDataSource,
    setSelectedDocumentId,
    setSelectedSectionKey,
    setIsJustEditModalOpen,
    editModalUpdated,
    setEditModalUpdated,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const { userTypeContext, permissionsState, clientPermissionsState } = useContext(PermissionsContext);
    const isClientUser = userTypeContext?.toString() === Constants.UserType.Client;
    const hideStatusBadge = clientPermissionsState?.CantSeeStatusDocuments && isClientUser;
    const canProcessStatus = canProcessDocumentStatus(userTypeContext, permissionsState, hideStatusBadge);
    const {
        onPCNRefresh,
        setPcnRefreshClientDetails,
        setPcnRefreshDetails,
        refreshUnprocessedPreview,
        accountCardOptions: contextAccountCardOptions,
        sortCodeIdOptions: contextSortCodeIdOptions,
        setEditModalUpdated: setEditModalUpdatedFromContext,
    } = useHamburgerMenu();
    const setEditModalUpdatedFn = setEditModalUpdated || setEditModalUpdatedFromContext;

    // When opened above Chat (z-index 1300), Dialog is raised (e.g. 2000).
    // MUI Autocomplete/DatePicker poppers default to ~1300 and would open behind the Dialog.
    const resolvedDialogZIndex = Number(dialogZIndex) > 0
        ? Number(dialogZIndex)
        : (onSelectDocument ? 2000 : 2);
    // Keep poppers above Chat (1300) and above an elevated Dialog (e.g. 2000).
    const overlayPopperZIndex = Math.max(resolvedDialogZIndex + 50, 2100);
    const withDatePickerSlotProps = useCallback((textFieldProps) => ({
        textField: {
            ...textFieldProps,
            fullWidth: false,
            className: ['txn-search-date-field', textFieldProps.className].filter(Boolean).join(' '),
        },
        popper: { sx: { zIndex: overlayPopperZIndex } },
        desktopPaper: { sx: { zIndex: overlayPopperZIndex } },
    }), [overlayPopperZIndex]);

    /* options for MUI lists ----------------------- */
    const debitCreditOpts = useMemo(
        () =>
            accountsData.map((o) => {
                const accountCodeId = o.cardId ?? o.accountCodeId ?? "";
                const accountCaseId = o.accountCaseId || "";
                const name = o.name || "";
                const label = [accountCodeId, name, accountCaseId]
                    .filter((part) => part !== undefined && part !== null && String(part).trim() !== "")
                    .join(" - ");
                return {
                    value: accountCodeId,
                    label,
                    name,
                    cardId: accountCodeId,
                    accountCodeId,
                    accountCaseId,
                };
            }),
        [accountsData]
    );

    const sortCodeOpts = useMemo(
        () =>
            sortCodes.map((s) => ({
                value: s.sortCodeId,
                label: `${s.sortCodeId} - ${s.sortCodeName}`,
            })),
        [sortCodes]
    );

    /* form filters -------------------------------- */
    const defaultFilters = (cId, accId) => ({
        caseId: cId,
        accountantId: accId,
        fromDate: null,
        toDate: null,
        uploadDateFrom: null,
        uploadDateTo: null,
        reportingMonthFrom: null,
        reportingMonthTo: null,
        registrationMonthFrom: null,
        registrationMonthTo: null,
        minAmount: "",
        maxAmount: "",
        sortCodeId: "",
        debitAccount: "",
        creditAccount: "",
        descriptionContains: "",
        referenceContains: "",
        reference2Contains: "",
        referenceFrom: "",
        referenceTo: "",
        referenceMode: "exact", // exact | range | include
        accountCardFrom: "",
        accountCardTo: "",
        confirmationNumber: "",
        uploadNumberFrom: "",
        uploadNumberTo: "",
        accountCaseId: "",
        includeIncomes: true,
        includeExpenses: true,
        includeJournalEntries: true,
        includePaymentAcceptances: true,
    });

    const filterCriteriaOptions = useMemo(() => [
        { key: "documentDate", label: t("DocumentDate") || t("DocumentDateFrom") || "Document Date" },
        { key: "uploadDate", label: t("UploadDate") || t("UploadDateFrom") || "Upload Date" },
        { key: "reportingMonth", label: t("ReportingMonth") || t("ReportingMonthFrom") || "Reporting Month" },
        { key: "amount", label: t("Amount") || "Amount" },
        { key: "sortCode", label: t("SortCode") || "Sort Code" },
        { key: "debitAccount", label: t("Debit Account") || "Debit Account" },
        { key: "creditAccount", label: t("Credit Account") || "Credit Account" },
        { key: "description", label: t("Description") || "Description" },
        { key: "reference", label: t("Reference") || "Reference" },
        { key: "reference2", label: t("Reference2") || "Reference 2" },
        { key: "accountCardRange", label: t("AccountCardRange") || "Account Card Range" },
        { key: "confirmationNumber", label: t("ConfirmationNumber") || "Confirmation Number" },
        { key: "uploadNumber", label: t("UploadNumber") || t("UploadNumberFrom") || "Upload Number" },
        { key: "accountCaseId", label: t("AccountCaseId") || "Account Case Id" },
    ], [t]);

    const filterSelectOptions = useMemo(
        () => filterCriteriaOptions.map((o) => ({ value: o.key, label: o.label })),
        [filterCriteriaOptions]
    );

    const referenceModeOptions = useMemo(() => [
        { value: "exact", label: t("ReferenceExact") || "Exact" },
        { value: "range", label: t("ReferenceRange") || "Range" },
        { value: "include", label: t("ReferenceInclude") || t("FilterContains") || "Contains" },
    ], [t]);

    /* ---------------- initial state ------------------------------------------ */
    const savedFilterPrefs = useMemo(() => loadSavedFilterPrefs(), []);
    const [filters, setFilters] = useState(() => ({
        ...defaultFilters(caseId, accountantId),
        ...(savedFilterPrefs?.referenceMode ? { referenceMode: savedFilterPrefs.referenceMode } : {}),
    }));
    const [activeFilterKeys, setActiveFilterKeys] = useState(
        () => savedFilterPrefs?.activeFilterKeys || [...DEFAULT_ACTIVE_FILTERS]
    );
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const isFilterActive = useCallback(
        (key) => activeFilterKeys.includes(key),
        [activeFilterKeys]
    );

    // Persist which filters the user chose (and reference mode)
    useEffect(() => {
        saveFilterPrefs(activeFilterKeys, filters.referenceMode || "exact");
    }, [activeFilterKeys, filters.referenceMode]);

    const handleActiveFiltersChange = useCallback((_, selected) => {
        let nextKeys = (selected || []).map((o) => o.key ?? o.value);
        // Migrate legacy "referenceRange" selection into unified "reference"
        if (nextKeys.includes("referenceRange")) {
            nextKeys = [...new Set(nextKeys.filter((k) => k !== "referenceRange").concat("reference"))];
            setFilters((prev) => ({ ...prev, referenceMode: "range" }));
        }
        const removed = activeFilterKeys.filter((k) => !nextKeys.includes(k));
        if (removed.length) {
            const cleared = removed.reduce(
                (acc, key) => ({ ...acc, ...(FILTER_FIELD_RESETS[key] || {}) }),
                {}
            );
            setFilters((prev) => ({ ...prev, ...cleared }));
        }
        setActiveFilterKeys(nextKeys);
    }, [activeFilterKeys]);

    const handleReferenceModeChange = useCallback((_, mode) => {
        if (!mode) return;
        setFilters((prev) => ({
            ...prev,
            referenceMode: mode,
            ...(mode === "range"
                ? { referenceContains: "" }
                : { referenceFrom: "", referenceTo: "" }),
        }));
    }, []);

    useEffect(() => {
        /* Keep selected filter criteria; only refresh case-bound form values */
        const prefs = loadSavedFilterPrefs();
        setFilters({
            ...defaultFilters(caseId, accountantId),
            ...(prefs?.referenceMode ? { referenceMode: prefs.referenceMode } : {}),
        });
        if (prefs?.activeFilterKeys?.length) {
            setActiveFilterKeys(prefs.activeFilterKeys);
        }

        /* Clear previous search results and close edit/PA modals */
        setResult(null);
        setEditModalOpen(false);
        setPaModalOpen(false);
        setEditedItem(null);
        setAddEditableItem({
            checks: [],
            creditCards: [],
            cash: [],
            bankTransactions: [],
            offsets: [],
            date: new Date(),
        });
        setLoading(false);
        setActionType('');
        setItemIdToDelete({ itemId: null, type: null });
    }, [caseId]);  // 🔁 runs every time the parent supplies a different caseId

    /** Map UnprocessedItems docType to sectionKey (Checks -> PaymentAcceptances) */
    const sectionKeyForDocType = (docType) => {
        const map = { Incomes: 'Incomes', Expenses: 'Expenses', JournalEntries: 'JournalEntries', Checks: 'PaymentAcceptances' };
        return map[docType] || docType;
    };
    /** When opening from UnprocessedItems: full list in row order (already filtered by type/search in UnprocessedItems). For next/prev we use this list so order follows rows and can mix types. */
    const navigationList = useMemo(() => (editModalDataSource?.length ? editModalDataSource : []), [editModalDataSource]);
    /** Legacy: same-type-only list when not using navigationList (e.g. opened from search) */
    const editModalList = useMemo(() => {
        if (!editModalDataSource?.length || !selectedSectionKey) return [];
        return editModalDataSource.filter(item => sectionKeyForDocType(item.docType) === selectedSectionKey);
    }, [editModalDataSource, selectedSectionKey]);

    // Handle openJustEditModal flag
    useEffect(() => {
        if (openJustEditModal && selectedDocumentId && selectedSectionKey) {
            const dataSourceItem = editModalDataSource?.find(r => r.id === selectedDocumentId);
            const mockRow = { id: selectedDocumentId, ...(dataSourceItem || {}) };
            internalRowClick(mockRow, selectedSectionKey);
            if (navigationList.length) {
                const idx = navigationList.findIndex(r => r.id === selectedDocumentId);
                setCurrentNavigationIndex(idx >= 0 ? idx : -1);
            } else {
                setCurrentNavigationIndex(-1);
                setCurrentEditIndex(-1);
                setCurrentPaIndex(-1);
            }
            if (!editModalDataSource?.length) onClose();
        }
    }, [openJustEditModal, selectedDocumentId, selectedSectionKey, navigationList, editModalDataSource]);

    
    const fetchData = useCallback(async () => {
        if (!caseId) return;
        setLoading(true);
        try {
            const active = new Set(activeFilterKeys);
            const data = await DocumentService.TransactionSearch(
                {
                    caseId,
                    accountantId,
                    includeIncomes: filters.includeIncomes,
                    includeExpenses: filters.includeExpenses,
                    includeJournalEntries: filters.includeJournalEntries,
                    includePaymentAcceptances: filters.includePaymentAcceptances,
                    fromDate: active.has("documentDate") ? filters.fromDate : null,
                    toDate: active.has("documentDate") ? filters.toDate : null,
                    uploadDateFrom: active.has("uploadDate") ? filters.uploadDateFrom : null,
                    uploadDateTo: active.has("uploadDate") ? filters.uploadDateTo : null,
                    reportingMonthFrom: active.has("reportingMonth") ? filters.reportingMonthFrom : null,
                    reportingMonthTo: active.has("reportingMonth") ? filters.reportingMonthTo : null,
                    registrationMonthFrom: filters.registrationMonthFrom,
                    registrationMonthTo: filters.registrationMonthTo,
                    minAmount: active.has("amount") && filters.minAmount ? parseFloat(filters.minAmount) : null,
                    maxAmount: active.has("amount") && filters.maxAmount ? parseFloat(filters.maxAmount) : null,
                    uploadNumberFrom: active.has("uploadNumber") && filters.uploadNumberFrom ? parseInt(filters.uploadNumberFrom) : null,
                    uploadNumberTo: active.has("uploadNumber") && filters.uploadNumberTo ? parseInt(filters.uploadNumberTo) : null,
                    sortCodeId: active.has("sortCode") ? (filters.sortCodeId || null) : null,
                    debitAccount: active.has("debitAccount") ? (filters.debitAccount || null) : null,
                    creditAccount: active.has("creditAccount") ? (filters.creditAccount || null) : null,
                    descriptionContains: active.has("description") ? (filters.descriptionContains || null) : null,
                    referenceContains:
                        active.has("reference") && filters.referenceMode !== "range"
                            ? (filters.referenceContains || null)
                            : null,
                    referenceMatchMode:
                        active.has("reference") && filters.referenceMode !== "range"
                            ? (filters.referenceMode === "include" ? "Contains" : "Exact")
                            : null,
                    reference2Contains: active.has("reference2") ? (filters.reference2Contains || null) : null,
                    referenceFrom:
                        active.has("reference") && filters.referenceMode === "range"
                            ? (filters.referenceFrom || null)
                            : null,
                    referenceTo:
                        active.has("reference") && filters.referenceMode === "range"
                            ? (filters.referenceTo || null)
                            : null,
                    accountCardFrom: active.has("accountCardRange") ? (filters.accountCardFrom || null) : null,
                    accountCardTo: active.has("accountCardRange") ? (filters.accountCardTo || null) : null,
                    confirmationNumber: active.has("confirmationNumber") ? (filters.confirmationNumber || null) : null,
                    accountCaseId: active.has("accountCaseId") ? (filters.accountCaseId || null) : null,
                },
                token
            );
            setResult(data);
        } catch (err) {
            console.error(err);
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [filters, activeFilterKeys, caseId, accountantId, token]);

    // useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

    const sections = [
        { key: "Incomes", title: t("Incomes"), data: result?.incomes ?? [] },
        { key: "Expenses", title: t("Expenses"), data: result?.expenses ?? [] },
        { key: "JournalEntries", title: t("JournalEntries"), data: result?.journalEntries ?? [] },
        { key: "PaymentAcceptances", title: t("PaymentAcceptances"), data: result?.paymentAcceptances ?? [] },
    ];

    /* ============================= Row-click dispatcher ============================= */
    /** states for the two edit modals **/
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [currentEditIndex, setCurrentEditIndex] = useState(-1);
    const [currentPaIndex, setCurrentPaIndex] = useState(-1);
    /** When opening from UnprocessedItems: index in navigationList (full row order, any type) for next/prev */
    const [currentNavigationIndex, setCurrentNavigationIndex] = useState(-1);
    const [paModalOpen, setPaModalOpen] = useState(false);
    const [editedItem, setEditedItem] = useState(null);
    const [oldAccountName, setOldAccountName] = useState({});
    const [oldAccountCaseId, setOldAccountCaseId] = useState({});
    const [oldAccountCardId, setOldAccountCardId] = useState({});
    const [originalTaxClearance, setOriginalTaxClearance] = useState(null);
    const [addEditableItem, setAddEditableItem] = useState({
        checks: [],
        creditCards: [],
        cash: [],
        bankTransactions: [],
        offsets: [],
        date: new Date()
    });
    const [accountType, setAccountType] = useState(null);
    const [paymentAcceptanceItems, setPaymentAcceptanceItems] = useState([]);
    const [statusCheck, setStatusCheck] = useState(false);
    const [clientItems, setClientItems] = useState([]);
    const [formattedPalCodeOptions, setFormattedPalCodeOptions] = useState([]);
    const [subAccountantOptions, setSubAccountantOptions] = useState([]);
    const [itemIdToDelete, setItemIdToDelete] = useState({ itemId: null, type: null });
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    const [actionType, setActionType] = useState('');
    const [accountCodeId, setAccountCodeId] = useState('');
    const [accountCodeName, setAccountCodeName] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [palCode, setPalCode] = useState('');
    const [showAccountCardInputsEdit, setShowAccountCardInputsEdit] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [oldSelectedAccountCardItem, setOldSelectedAccountCardItem] = useState(null);

    /* -------- stubbed parent-level callbacks & states (replace with real ones) ----- */
    /* EditModal required props */
    const fade = false;

    const handleAccountChange = (selectedOption, field, CustomDropdownFlag = false) => {
        if (field === 'debitAccount' || field === 'creditAccount') {
            const accountCardEditDownload = accountCardOptions.find(option => option.cardId === selectedOption.value);
            const name = accountCardEditDownload?.name;
            const accountCaseId = accountCardEditDownload?.accountCaseId;
            const docType = getDocType(editedItem);
            const updatedItem = docType === 'Incomes' 
                ? { ...editedItem, accountCodeName: name, accountCodeId: selectedOption.value, debitAccount: selectedOption.value, accountCaseId: accountCaseId }
                : { ...editedItem, accountCodeName: name, accountCodeId: selectedOption.value, creditAccount: selectedOption.value, accountCaseId: accountCaseId };
            setEditedItem(normalizeItem(updatedItem));
        } else if (field === 'accountCodeId') {
            if (selectedOption.value1 === 'AddItem') {
                setEditedAddAccountCard(true);
                setOldAccountName(editedItem.accountCodeName);
                setOldAccountCaseId(editedItem.accountCaseId);
                setOldAccountCardId(editedItem.accountCodeId);
                setEditedItem(normalizeItem({ ...editedItem, accountCodeName: '', accountCodeId: '', accountCaseId: '' }));
            }
            else {
                const accountCardEdit = accountCardOptions.find(option =>
                    option.cardId === (CustomDropdownFlag ? selectedOption.cardId : selectedOption.value)
                );
                const name = accountCardEdit?.name;
                const accountCaseId = accountCardEdit?.accountCaseId;
                const cardId = CustomDropdownFlag ? selectedOption.cardId : selectedOption.value;
                const docType = getDocType(editedItem);
                const updatedItem = docType === 'Incomes'
                    ? { ...editedItem, accountCodeName: name, accountCodeId: cardId, debitAccount: cardId, accountCaseId: accountCaseId }
                    : { ...editedItem, accountCodeName: name, accountCodeId: cardId, creditAccount: cardId, accountCaseId: accountCaseId };
                setEditedItem(normalizeItem(updatedItem));
            }
        }
    };
    const isRange = false;
    const isClosedReportingMonth = false;

    // Use context values if available, otherwise fall back to props
    const effectiveAccountsData = contextAccountCardOptions && contextAccountCardOptions.length > 0 
        ? contextAccountCardOptions 
        : accountsData;
    const effectiveSortCodes = contextSortCodeIdOptions && contextSortCodeIdOptions.length > 0 
        ? contextSortCodeIdOptions 
        : sortCodes;
    
    const [accountCardOptions, setAccountCardOptions] = useState(effectiveAccountsData || []);

    // Update when context or props change
    useEffect(() => { 
        if (contextAccountCardOptions && contextAccountCardOptions.length > 0) {
            setAccountCardOptions(contextAccountCardOptions);
        } else if (accountsData) {
            setAccountCardOptions(accountsData);
        }
    }, [contextAccountCardOptions, accountsData]);
    /** EditModal dropdown options with "Add account card" first (same as Document/JournalEntry) */
    const accountCardOptionsWithAdd = useMemo(
        () => [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(accountCardOptions || [])],
        [accountCardOptions, t]
    );
    const handleChange = (field, value) => {
        const updatedValue = value;

        if (field === 'sortCodeId') {



            if (!value || !sortCodeIdOptions.some((option) => option.sortCodeId === value)) {
                // Set default values for the empty state
                const updatedItem = {
                    ...editedItem,
                    vat: 100, // Default VAT
                    [field]: '',
                };
                setEditedItem(normalizeItem(updatedItem));
                return; // Exit early since there's no valid sortCodeId
            }

            const name = sortCodeIdOptions.find(
                (option) => option.sortCodeId === value
            )?.sortCodeName || ''

            // Extract the name without the parentheses
            const nameWithoutParentheses = name.replace(/\s*\(.*?\)\s*/, '').trim();

            // Check for a number within parentheses
            const match = name.match(/\((\d+)%?/);
            let customVat = match ? parseInt(match[1], 10) : null;


            const sortCode = sortCodeIdOptions.filter(sc => sc.sortCodeId === updatedValue)[0];

            // Determine the VAT based on the name without parentheses
            const newVat = sortCode?.vat ?? 100;


            const accountCard = accountCardOptions.find(
                (item) => item.cardId === (editedItem.documentType === 'Incomes' ? sortCode.debitAccount : sortCode.creditAccount)
            );

            const debit = accountCardOptions.find(
                (item) => item.cardId === sortCode.debitAccount
            );

            const credit = accountCardOptions.find(
                (item) => item.cardId === sortCode.creditAccount
            );



            if (value === '1') { //hova
                const updatedItem = {
                    ...editedItem,
                    vat: newVat,
                    [field]: updatedValue,
                    creditAccount: null,
                    debitAccount: debit ? debit.cardId : editedItem?.debitAccount,
                    accountCodeName: accountCard ? accountCard.name : editedItem.accountCodeName,
                    accountCodeId: accountCard ? accountCard.cardId : editedItem.accountCodeId,
                    accountCaseId: accountCard ? accountCard.accountCaseId : editedItem.accountCaseId,
                };
                setEditedItem(normalizeItem(updatedItem));
                return;
            }
            if (value === '2') { //z5ot
                const updatedItem = {
                    ...editedItem,
                    vat: newVat,
                    [field]: updatedValue,
                    creditAccount: credit ? credit.cardId : editedItem?.creditAccount,
                    debitAccount: null,
                    accountCodeName: accountCard ? accountCard.name : editedItem.accountCodeName,
                    accountCodeId: accountCard ? accountCard.cardId : editedItem.accountCodeId,
                    accountCaseId: accountCard ? accountCard.accountCaseId : editedItem.accountCaseId,
                };
                setEditedItem(normalizeItem(updatedItem));
                return;
            }

            const docType = getDocType(editedItem);
            if (docType === 'JournalEntries') {
                // For journal entries, sync accountCaseId based on sort code VAT type
                const sortCodeVatTypeLabel = Constants.SortCodeVatType?.[sortCode?.sortCodeVatType];
                const journalSortCodeIsIncome = sortCodeVatTypeLabel === 'VatTransactions';
                const journalSortCodeIsExpense = sortCodeVatTypeLabel === 'VatExpenses';
                
                let syncedAccountCaseId = editedItem.accountCaseId;
                
                if (journalSortCodeIsIncome && sortCode.debitAccount) {
                    const debitAccountCard = accountCardOptions.find(option => option.cardId === sortCode.debitAccount);
                    syncedAccountCaseId = debitAccountCard?.accountCaseId ?? debitAccountCard?.caseId ?? editedItem.accountCaseId;
                } else if (journalSortCodeIsExpense && sortCode.creditAccount) {
                    const creditAccountCard = accountCardOptions.find(option => option.cardId === sortCode.creditAccount);
                    syncedAccountCaseId = creditAccountCard?.accountCaseId ?? creditAccountCard?.caseId ?? editedItem.accountCaseId;
                }

                const updatedItem = {
                    ...editedItem,
                    vat: newVat,
                    [field]: updatedValue,
                    creditAccount: sortCode.creditAccount ? sortCode.creditAccount : editedItem.creditAccount,
                    debitAccount: sortCode.debitAccount ? sortCode.debitAccount : editedItem.debitAccount,
                    accountCaseId: syncedAccountCaseId,
                };
                setEditedItem(normalizeItem(updatedItem));
            }
            else {
                const updatedItem = {
                    ...editedItem,
                    vat: newVat,
                    [field]: updatedValue,
                    creditAccount: credit ? credit.cardId : editedItem?.creditAccount,
                    debitAccount: debit ? debit.cardId : editedItem?.debitAccount,
                    accountCodeName: accountCard ? accountCard.name : editedItem.accountCodeName,
                    accountCodeId: accountCard ? accountCard.cardId : editedItem.accountCodeId,
                    accountCaseId: accountCard ? accountCard.accountCaseId : editedItem.accountCaseId,
                };
                setEditedItem(normalizeItem(updatedItem));
            }


        }
        else if (field === 'creditAccount' || field === 'debitAccount') {

            // Use the helper function to update account fields for credit or debit account
            const newValue = accountCardOptions.find(option => option.cardId === updatedValue) || null;
            updateAccountFields(newValue, field);
        }
        else {
            // For fields other than sortCodeId, just update the edited item
            const updatedItem = {
                ...editedItem,
                [field]: updatedValue,
            };
            setEditedItem(normalizeItem(updatedItem));
        }
    };

    // Helper to get docType, fallback to documentType if docType doesn't exist
    const getDocType = (item) => item?.docType || item?.documentType || '';
    
    // Helper to normalize item - ensure docType is set from documentType if docType doesn't exist
    const normalizeItem = (item) => {
        if (!item) return item;
        if (!item.docType && item.documentType) {
            return { ...item, docType: item.documentType };
        }
        return item;
    };

    const updateAccountFields = (newValue, accountType) => {
        if (newValue?.value1 === 'AddItem') {
            // handleNoOptionCreditDebitClick('', accountType, getDocType(editedItem));
            return;
        }

        // Validation: Credit and Debit accounts must be different
        if (accountType === 'creditAccount' && newValue?.cardId && newValue.cardId === editedItem.debitAccount) {
            toast.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
            return; // Don't update the state
        }
        if (accountType === 'debitAccount' && newValue?.cardId && newValue.cardId === editedItem.creditAccount) {
            toast.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
            return; // Don't update the state
        }

        const docType = getDocType(editedItem);
        const isJournalEntry = docType === 'JournalEntries';
        
        // For journal entries, determine which account should sync accountCaseId based on sort code VAT type
        let shouldSyncAccountCaseOnly = false;
        if (isJournalEntry && editedItem.sortCodeId) {
            const sortCode = sortCodeIdOptions?.find((option) => option.sortCodeId === editedItem.sortCodeId);
            if (sortCode) {
                const sortCodeVatTypeLabel = Constants.SortCodeVatType?.[sortCode.sortCodeVatType];
                const journalSortCodeIsIncome = sortCodeVatTypeLabel === 'VatTransactions';
                const journalSortCodeIsExpense = sortCodeVatTypeLabel === 'VatExpenses';
                
                // Sync accountCaseId for debit account if sort code is income type, or credit account if expense type
                shouldSyncAccountCaseOnly = 
                    (journalSortCodeIsIncome && accountType === 'debitAccount') ||
                    (journalSortCodeIsExpense && accountType === 'creditAccount');
            }
        }

        const isIncomesDebit = docType === 'Incomes' && accountType === 'debitAccount';
        const isExpensesCredit = docType === 'Expenses' && accountType === 'creditAccount';
        const shouldSyncPrimaryFields = isIncomesDebit || isExpensesCredit;

        const nextAccountId = newValue ? newValue.cardId : '';
        const nextAccountName = newValue ? newValue.name : '';
        const nextAccountCaseId = newValue ? (newValue.accountCaseId ?? newValue.caseId ?? '') : '';

        // Update the edited item with the new fields
        setEditedItem((prev) => {
            const updatedItem = {
                ...prev,
                [accountType]: nextAccountId,
            };

            if (shouldSyncPrimaryFields) {
                updatedItem.accountCodeId = nextAccountId;
                updatedItem.accountCodeName = nextAccountName;
                updatedItem.accountCaseId = nextAccountCaseId;
            } else if (shouldSyncAccountCaseOnly) {
                updatedItem.accountCaseId = nextAccountCaseId;
            }

            // Normalize item to ensure docType is set from documentType if docType doesn't exist
            return normalizeItem(updatedItem);
        });
    };

    const handleDateChange = (e, field) => {
        setEditedItem(normalizeItem({ ...editedItem, [field]: e }));
    };
    const setReportingMonthDate = async (selectedDate, field) => {
        if (!selectedDate) {
            return;
        }
        const newDate = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), 10));
        const response1 = await ClientService.CheckIfMonthClosed(client.caseId, newDate, token);
        if (response1.ok) {
            const isClosedReportingMonth = await response1.json();
            if (isClosedReportingMonth) {
                toast.info(t('MonthReportAlreadyClosedTryAgain'));
                return
            }
            else {
                setEditedItem(normalizeItem({ ...editedItem, [field]: newDate }));
            }
        }
    }
    const handleVatRadioChange = (e) => {
        setEditedItem(normalizeItem({ ...editedItem, ["vat"]: parseFloat(e.target.value) }));
    };
    const handleRadioChange = async (e, options = {}) => {
        if (!canProcessStatus) return;
        if (e.target.value?.toString() === 'done' && !options?.validationAlreadyDone) {

            const isValid = await validateDocumentBeforeProcessing(editedItem, t, client, options?.forceProcess === true);
            if (!isValid) return;
        }

        if (e.target.value?.toString() === 'done') {
            setStatusCheck(true);
        }
        else if (e.target.value?.toString() === 'notDone') {
            setStatusCheck(false);
        }

        setEditedItem(normalizeItem({ ...editedItem, status: e.target.value }));
    };

    const handlePaymentAcceptanceRadioChange = async (e) => {
        if (!canProcessStatus) return;
        if (e.target.value?.toString() === 'done') {
            const isValid = await validatePaymentAcceptanceItems(addEditableItem, t, client);
            if (!isValid) return;

            setStatusCheck(true);
        }
        else if (e.target.value?.toString() === 'notDone') {
            setStatusCheck(false);
        }
        setAddEditableItem({ ...addEditableItem, ["status"]: e.target.value });
    };
    const [editedAddAccountCard, setEditedAddAccountCard] = useState(false);
    const handleCloseEditAddNewAccountCardDocument = () => {
        setEditedItem(normalizeItem({ ...editedItem, accountCodeName: oldAccountName, accountCodeId: oldAccountCardId, accountCaseId: oldAccountCaseId }));
        setEditedAddAccountCard(false);
    };
    const handleEditAddAccountChange = (selectedOption, field) => {
        if (field === 'AccountCodeId') {
            if (selectedOption?.value1 === 'AddItem') {
                setEditedAddAccountCard(true);
                setOldAccountName(editedItem.accountCodeName);
                setOldAccountCaseId(editedItem.accountCaseId);
                setOldAccountCardId(editedItem.accountCodeId);
                setEditedItem(normalizeItem({ ...editedItem, accountCodeName: '', accountCodeId: '', accountCaseId: '' }));
                return;
            }
            const docType = getDocType(editedItem);
            const cardId = selectedOption?.cardId ?? selectedOption?.value ?? selectedOption;
            const accountCardEdit = accountCardOptions.find(opt => opt.cardId === cardId || opt.cardId === selectedOption);
            const name = accountCardEdit?.name ?? selectedOption?.name;
            const accountCaseId = accountCardEdit?.accountCaseId ?? selectedOption?.accountCaseId;
            const updatedItem = docType === 'Incomes'
                ? { ...editedItem, accountCodeName: name, accountCodeId: cardId, debitAccount: cardId, accountCaseId: accountCaseId }
                : { ...editedItem, accountCodeName: name, accountCodeId: cardId, creditAccount: cardId, accountCaseId: accountCaseId };
            setEditedItem(normalizeItem(updatedItem));
        }
        else if (field === 'accountCodeName') {
            setEditedItem(normalizeItem({ ...editedItem, accountCodeName: selectedOption }));
        }
        else {
            setEditedItem(normalizeItem({ ...editedItem, accountCaseId: selectedOption }));
        }
    };
    const sortCodeIdOptions = effectiveSortCodes;
    const debitAccountOptions = effectiveAccountsData;
    const creditAccountOptions = effectiveAccountsData;
    const handleUpdate = async (itemIdOrItem) => {
        const itemToSave = (typeof itemIdOrItem === 'object' && itemIdOrItem != null && itemIdOrItem.id) ? itemIdOrItem : editedItem;
        // setEditedPopupItem(false);
        setLoading(true);
        const documentUrl = itemToSave.documentUrl;


        try {
            if (itemToSave.status === 'done') {
                const isValid = await validateDocumentBeforeProcessing(itemToSave, t, client, !!itemToSave.forceProcess);
                if (!isValid) {
                    setLoading(false);
                    return;
                }

            }
            delete itemToSave.documentUrl;

            const response = await DocumentService.UpdateDocument(itemToSave.documentType, token, itemToSave);
            if (canProcessStatus) {
                if (itemToSave.status == 'done') {
                    await handleDoneClick(itemToSave.documentType, itemToSave.id);
                    // handleSortChange('id', 'newest', 'descending');
                }
                else if (itemToSave.status == 'notDone') {
                    await handleNotDoneClick(itemToSave.documentType, itemToSave.id)
                }
            }



            if (response.ok) {
                // Reassign documentUrl back to editedItem after successful update
                editedItem.documentUrl = documentUrl;

                // When opened from UnprocessedItems (editModalDataSource), keep edit modal open like Document.jsx
                const keepModalOpenFromUnprocessed = openJustEditModal && editModalDataSource?.length > 0;
                if (!keepModalOpenFromUnprocessed) {
                    handleCancel();
                }
                RefreshPage();
                if (openJustEditModal) {
                    setPcnRefreshClientDetails(true);
                    setPcnRefreshDetails(true);
                }
                if (keepModalOpenFromUnprocessed && refreshUnprocessedPreview) {
                    refreshUnprocessedPreview();
                }
                toast.success(t("documentUpdateSuccess"), { position: "top-left" });
                if (setEditModalUpdatedFn) setEditModalUpdatedFn(true);

            } else {
                // Reassign documentUrl back to editedItem even on error to maintain state
                editedItem.documentUrl = documentUrl;

                const errorText = await response.text();
                // Handle error

                // Show error toast
                toast.error(t("documentUpdateError"));
            }
        } catch (error) {
            // Handle error
            console.error(error);

            // Show error toast
            setEditedItem({});
            toast.error(t("documentUpdateError"));
            // Note: documentUrl not reassigned in catch block since editedItem is cleared
        }
        // setEditingRow(null);
        // setEditedItem({});
        setEditedAddAccountCard(false);
        setLoading(false);
        if (setEditModalUpdatedFn) setEditModalUpdatedFn(true);
        // Refresh or update your data list
    };
    const handleCancel = () => {

        setEditedItem({});
        setEditModalOpen(false);
        setAddEditableItem({
            checks: [],
            creditCards: [],
            cash: [],
            bankTransactions: [],
            offsets: [],
            date: new Date()
        })
        setPaModalOpen(false);
        
        // Reset the state variables when canceling
        // Only close parent modal if it was opened via openJustEditModal
        if (setSelectedDocumentId) setSelectedDocumentId(null);
        if (setSelectedSectionKey) setSelectedSectionKey(null);
        if (setIsJustEditModalOpen && openJustEditModal) 
        {
            // Only close parent modal if it was opened directly for editing
            setIsJustEditModalOpen(false);
            onClose();
        }
    };

    const handleConfirm = async () => {
        if (actionType === 'delete') {
            await handleConfirmDelete();
        } else if (actionType === 'deleteAccountCard') {
            await handleConfirmDeleteAccountCard();
        }
    };

    const getConfirmMessage = () => {
        switch (actionType) {
            case 'delete':
                return t('confirmDeleteMessage');
            case 'deleteAccountCard':
                return t('CannotDeleteAccountCard') || t('confirmDeleteMessage');
            default:
                return t('confirmDeleteMessage');
        }
    };

    const getTitleMessage = () => {
        switch (actionType) {
            case 'delete':
                return t('confirmDeleteTitle');
            case 'deleteAccountCard':
                return t('confirmDeleteTitle');
            default:
                return t('confirmDeleteTitle');
        }
    };

    const handleConfirmDeleteAccountCard = async () => {
        if (itemIdToDelete?.type !== 'accountCard' || !itemIdToDelete?.itemId) return;
        setLoading(true);
        setIsConfirmationVisible(false);
        setActionType('');
        setItemIdToDelete({ itemId: null, type: null });
        try {
            const response = await EnumsService.DeleteAccountCardsbyCaseId(client.caseId, itemIdToDelete.itemId, token);
            if (response.ok) {
                setAccountCardOptions(prev => (prev || []).filter(card => card.cardId !== itemIdToDelete.itemId));
                toast.success(t('CustomerSupplierDeletedSuccessfully') || t('documentDeleteSuccess'));
            } else {
                toast.error(t('documentDeleteError'));
            }
        } catch (err) {
            toast.error(t('documentDeleteError'));
        }
        setLoading(false);
    };


    const handleDeleteClick = (itemId, type) => {
        setItemIdToDelete({ itemId, type });
        setIsConfirmationVisible(true);
    };

    const handleEditDeleteClick = (itemId) => {
        handleDeleteClick(itemId, editedItem.documentType);
        setActionType('delete')
        setEditedItem({});
    };

    const handlePaDeleteClick = (itemId) => {
        handleDeleteClick(itemId, addEditableItem?.documentType || 'PaymentAcceptances');
        setActionType('delete');
    };
    const handleReopenEdit = async (item, index) => {
        await fetchEnums(user, token, client);
        // handleOpenEditSingleEntry(item);
    };

    const handleConfirmDelete = async () => {
        // Perform delete operation
        setLoading(true);
        setIsConfirmationVisible(false);
        try {
            const response = await DocumentService.DeleteDocument(itemIdToDelete.type, itemIdToDelete.itemId, token);
            if (response.ok) {
                handleCancel();

                RefreshPage()
                // Show success toast
                toast.success(t("documentDeleteSuccess"));
                if (setEditModalUpdatedFn) setEditModalUpdatedFn(true);
            } else {
                // Handle any errors or display an error toast

                const errorResponse2 = await response.text();


                if (errorResponse2 === 'Income is user-generated and cannot be deleted') {
                    toast.error(t("Income is user-generated and cannot be deleted"));
                } else {
                    toast.error(t("documentDeleteError"));
                }

            }
        } catch (error) {
            console.error('Error deleting document:', error);

            // Show error toast
            toast.error(t("documentDeleteError"));
        }
        setLoading(false);
    };
    const palCodes = formattedPalCodeOptions;
    const RefreshPage = async () => {
        handleCancel();

        // if (isRange) {
        //     await fetchDataOnDateRangeChange(new Date(startDate), new Date(endDate));
        // } else if (isBiMonthly) {
        //     await fetchDataOnDateRangeChange(new Date(biStartDate), new Date(biEndDate), true);
        // } else {
        //     await fetchDataOnDateRangeChange(new Date(date), new Date(date));
        // }

        await fetchData();
        await fetchEnums(user, token, client);
    };
    const handleAccountCardSave = async (AccountCodeId, AccountCodeName, CompanyId) => {
        const docType = getDocType(editedItem);
        const typeLabel = docType === 'Incomes' ? 'לקוחות' : 'ספקים';
        const res = await EnumsService.AddAccountCard(client.caseId, AccountCodeId, AccountCodeName, CompanyId, typeLabel, token);
        if (!res.ok) throw new Error(await res.text());
    };

    const fetchEnums = async (user, token, client) => {
        if (!client?.caseId) return;
        setLoading(true);
        try {
            // Initiate all fetch requests at once (include account cards so new cards appear in EditModal & PA modal)
            const [
                clientItemsResponse,
                palCodesResponse,
                subAccountantsResponse,
                accountantResponse,
                accountsResponse
            ] = await Promise.all([
                EnumsService.GetClientItemsByCaseId(client.caseId, token),
                EnumsService.GetPalCodesByAccountantId(client.accountantId, token),
                SubAccountantService.GetSubAccountantByAccountantId(token, user.accountantId),
                SubAccountantService.GetAccountantById(token, user.accountantId),
                EnumsService.GetAccountCardsByCaseId(client.caseId, token)
            ]);

            // Handle client items
            if (clientItemsResponse.ok) {
                const clientItemsData = await clientItemsResponse.json();
                clientItemsData.unshift({ makat: 'add-item', name: t('AddItem') });
                setClientItems(clientItemsData);
            }

            // Handle pal codes
            if (palCodesResponse.ok) {
                const palCodesData = await palCodesResponse.json();
                const formattedPalCodes = palCodesData.map(palCode => ({
                    label: palCode.palCodeId,
                    value: palCode.palCodeId
                }));
                setFormattedPalCodeOptions(formattedPalCodes);
            }

            // Handle account cards – so new card from AddAccountCardModal is available in EditModal & PaymentAcceptanceCreateEditModal
            if (accountsResponse?.ok) {
                const accountsData = await accountsResponse.json();
                setAccountCardOptions(Array.isArray(accountsData) ? accountsData : []);
            }

            // Handle subAccountants and accountant
            if (subAccountantsResponse) {
                const enhancedAccountantResult = {
                    ...accountantResponse,
                    subAccountantId: accountantResponse.accountantId, // match existing logic
                };

                const combinedSubAccountants = [enhancedAccountantResult, ...subAccountantsResponse];

                if (client) {
                    combinedSubAccountants.push({
                        subAccountantId: client.caseId,
                        name: client.name,
                    });
                }

                const combinedOptions = combinedSubAccountants.map((acc) => ({
                    value: acc.subAccountantId,
                    label: acc.name,
                }));

                setSubAccountantOptions(combinedOptions);
            } else {
                toast.error(t('fetchSubAccountantsDataError'));
            }

            // toast.success(t('enumFetchSuccess'), { position: "top-left" });
        } catch (error) {
            console.error("Error fetching enum data:", error);
            toast.error(t('enumFetchError', { errorText: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const assets = [];
    const handleAssetClicked = () => { };

    /* PA modal stub props */

    const setPaymentDate = async (date) => {
        const response1 = await ClientService.CheckIfMonthClosed(client.caseId, date, token);
        const isClosedReportingMonth = await response1.json();
        if (isClosedReportingMonth) {
            toast.error(t('MonthReportAlreadyClosedTryAgain'));
            return
        }
        setAddEditableItem((prevItem) => ({
            ...prevItem,
            date: date.toISOString()
        }));
    }
    const handleDescriptionChange = (event) => {
        setAddEditableItem((prevState) => ({
            ...prevState,
            description: event.target.value || ''
        }));
    };
    const handleChangeOriginalTaxClearance = (event) => {
        const raw = event.target.value?.trim() ?? '';
        const value = raw === '' ? 0 : Number(raw);
        setAddEditableItem(prev => ({ ...prev, withholdingTax: value }));
        setOriginalTaxClearance(value);
    };
    const handleAddEditableItemChange = (name, value) => {
        setAddEditableItem(prev => ({ ...prev, [name]: value }));

    };
    const handleItemChange = (type, index, field, value) => {
        setPaymentAcceptanceItems(prevItems => {
            let typeIndex = -1;
            const updatedItems = prevItems.map((item, idx) => {
                if (item.type === type) {
                    typeIndex += 1;
                    if (typeIndex === index) {
                        return { ...item, [field]: value };
                    }
                }
                return item;
            });

            // Update the corresponding field in addEditableItem
            setAddEditableItem(prevItem => {
                const updatedTypeItems = prevItem[type].map((item, idx) => {
                    if (idx === index) {
                        return { ...item, [field]: value };
                    }
                    return item;
                });

                // Conditionally update the fields based on payment type and field
                const paymentType = addEditableItem.paymentAcceptanceType;
                const shouldUpdateFields =
                    (paymentType === 'תקבול' && field === 'creditAccount') ||
                    (paymentType === 'תשלום' && field === 'debitAccount');

                const matchingOption = accountCardOptions.find(
                    option => option.cardId === value
                );

                const updatedFields = shouldUpdateFields && matchingOption
                    ? {
                        accountCaseId: matchingOption.accountCaseId || '',
                        accountCodeId: matchingOption.cardId || '',
                        accountCodeName: matchingOption.name || '',
                    }
                    : {};

                return { ...prevItem, ...updatedFields, [type]: updatedTypeItems };
            });

            return updatedItems;
        });
    };

    const handleRemoveItem = (type, index) => {

        // Update paymentAcceptanceItems by filtering the item at the given index
        const newItems = paymentAcceptanceItems.filter((item, idx) => !(item.type === type && idx === index));
        setPaymentAcceptanceItems(newItems);

        // Update addEditableItem safely
        setAddEditableItem(prevItem => {
            // Check if the type exists in prevItem before modifying
            if (!prevItem[type] || !Array.isArray(prevItem[type])) {
                return prevItem;
            }

            // Create a new filtered array
            const updatedTypeItems = [...prevItem[type]];

            updatedTypeItems.splice(index, 1); // Remove the element at the specified index

            return { ...prevItem, [type]: updatedTypeItems };
        });
    };
    const handleAddItem = (theType, suggestedAmount = '', accountFields = {}) => {
        const paymentType = addEditableItem.paymentAcceptanceType;

        const newItem = {
            type: theType,
            description: '',
            amount: suggestedAmount,
            paymentDate: new Date(),
            bankName: '',
            bankCode: '',
            bankBranchCode: '',
            supplierAccountNumber: '',
            bankTransactionsCode: Constants.PaymentAcceptanceConstantsAccountcards.GeneralBank,
            creditAccount: paymentType === 'תקבול' ? addEditableItem.accountCodeId || '' : '',
            debitAccount: paymentType === 'תשלום' ? addEditableItem.accountCodeId || '' : '',
            ...accountFields,
        };

        // Update paymentAcceptanceItems
        setPaymentAcceptanceItems(prevItems => [...prevItems, newItem]);

        // Ensure theType exists and is an array before spreading
        setAddEditableItem(prevItem => ({
            ...prevItem,
            [theType]: Array.isArray(prevItem[theType]) ? [...prevItem[theType], newItem] : [newItem]
        }));
    };

    const handleDoneClick = async (type, itemId) => {
        if (!canProcessStatus) {
            return;
        }

        setLoading(true);
        try {
            const response = await ClientDocumentCountService.removeDocumentByTypeAndId(client.caseId, client.accountantId, type, itemId, token);

            if (response.ok) {
                toast.success(t('DocumentProcessed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            } else {
                const errorText = await response.text();
                toast.error(t('DocumentNotProcessed', { errorText }), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            }
        } catch (error) {
            toast.error(t('DocumentNotProcessed', { errorText: error.message }), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        }
        setLoading(false);
    };

    const handleNotDoneClick = async (type, itemId) => {
        if (!canProcessStatus) {
            return;
        }
        setLoading(true);
        try {
            const response = await ClientDocumentCountService.addDocumentByTypeAndId(client.caseId, client.accountantId, type, itemId, token)
            if (response.ok) {
                toast.success(t('DocumentProcessed'), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            } else {
                const errorText = await response.text();
                toast.error(t('DocumentNotProcessed', { errorText }), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
            }
        } catch (error) {
            toast.error(t('DocumentNotProcessed', { errorText: error.message }), { position: direction === 'rtl' ? 'top-left' : 'top-right' });
        }
        setLoading(false);
    };

    const handleSavePaymentAcceptance = async () => {
        try {
            setLoading(true);
            // Same Update body as PaymentAcceptance.jsx — partial payloads omit offsets
            // and other scalars, which either wipe fields or crash AutoMapper on null collections.
            const updatedDocument = buildPaymentAcceptanceUpdatePayload(addEditableItem);

            if (addEditableItem.status === 'done') {
                const isValid = await validatePaymentAcceptanceItems(addEditableItem, t, client);
                if (!isValid) {
                    setLoading(false);
                    return;
                }

            }
            // Call the UpdateDocument function from DocumentService
            const response = await DocumentService.UpdateDocument(addEditableItem.documentType, token, updatedDocument);
            // if (addNewAccountCardDocument) {
            //     setAddNewAccountCardDocument(false);
            //     const response1 = await EnumsService.AddAccountCard(
            //         user.caseId,
            //         addEditableItem.accountCodeName,
            //         token,
            //         addEditableItem.accountCaseId
            //     );
            // }
            if (response.ok) {
                // Handle successful update
                if (canProcessStatus) {
                    if (addEditableItem.status === 'done') {
                        await handleDoneClick("Checks", addEditableItem.id);
                    } else if (addEditableItem.status === 'notDone') {
                        await handleNotDoneClick("Checks", addEditableItem.id);
                    }
                }
                toast.success(t('documentUpdateSuccess'), { position: 'top-left' });
                if (setEditModalUpdatedFn) setEditModalUpdatedFn(true);
                if (openJustEditModal && editModalDataSource?.length > 0 && refreshUnprocessedPreview) {
                    refreshUnprocessedPreview();
                }
            } else {
                toast.error(t('documentUpdateError'));
            }
        } catch (error) {
            console.error('documentUpdateError:', error);
            // Show an error toast message
            toast.error(t('documentUpdateError'));
        }
        await RefreshPage();
        // When opened from UnprocessedItems (editModalDataSource), keep PA modal open like Document.jsx
        if (!(openJustEditModal && editModalDataSource?.length > 0)) {
            closePaModal();
        }
        setLoading(false);


    };

    const [buttonDisabled, setButtonDisabled] = useState(true);
    useEffect(() => {
        let unionArray = [];
        if (addEditableItem) {
            unionArray = [
                ...(addEditableItem.checks || []),
                ...(addEditableItem.creditCards || []),
                ...(addEditableItem.cash || []),
                ...(addEditableItem.bankTransactions || [])
            ];
        }

        if (unionArray.length > 0) {
            const allAmountsFilled = unionArray.every(item => item.amount !== undefined && item.amount !== null && item.amount !== '');
            const allFieldsFilled = allAmountsFilled &&
                (addEditableItem.accountCaseId !== undefined && addEditableItem.accountCaseId !== null && addEditableItem.accountCaseId !== '' ||
                    addEditableItem.accountCodeId !== undefined && addEditableItem.accountCodeId !== null && addEditableItem.accountCodeId !== '' ||
                    addEditableItem.accountCodeName !== undefined && addEditableItem.accountCodeName !== null && addEditableItem.accountCodeName !== '') &&
                addEditableItem.paymentAcceptanceType !== undefined && addEditableItem.paymentAcceptanceType !== null && addEditableItem.accountType !== '';



            setButtonDisabled(!allFieldsFilled);
        } else {
            setButtonDisabled(true);
        }
    }, [addEditableItem, accountCodeId, accountType]);
    const handleMuiChange = (event, value) => {
        if (!value) {
            setAddEditableItem(prevState => ({
                ...prevState,
                accountCodeId: null,
            }));
            return;
        }


        const updatedFields = {
            accountCaseId: value.accountCaseId,
            accountCodeId: value.cardId,
            accountCodeName: value.name,
        };



        // Update addEditableItem state
        setAddEditableItem(prevState => ({
            ...prevState,
            ...updatedFields,
        }));
        // setSelectedOption({ ...value });

        // Call the function to update items
        updateAllItems(value, updatedFields);
    };
    const updateAllItems = (value, updatedFields) => {
        const updatedItems = ['checks', 'bankTransactions', 'creditCards', 'cash'];
        const paymentType = addEditableItem.paymentAcceptanceType;

        updatedItems.forEach(itemType => {
            addEditableItem[itemType]?.forEach((item, index) => {
                // Determine the field to update
                const fieldToUpdate = paymentType === 'תקבול' ? 'creditAccount' : 'debitAccount';

                handleItemChange(itemType, index, fieldToUpdate, value.cardId);
            });
        });

        // Update addEditableItem with new account data
        setAddEditableItem(prevState => ({
            ...prevState,
            ...updatedFields,
        }));
    };

    const handleEdit = () => { };
    const handleChangeAccountType = (selectedOption) => {
        setAccountType(selectedOption.value);
        setAddEditableItem((prevState) => ({
            ...prevState,
            paymentAcceptanceType: selectedOption ? selectedOption.value : ''
        }));
    };
    const [paymentBeingEdited, setPaymentBeingEdited] = useState(false);
    const CustomDropdown = () => null;
    const [addNewAccountCardDocument, setAddNewAccountCardDocument] = useState(false);
    const isClosedReportingMonthAndIsRange = false;
    const CustomPalCode = () => null;

    const setAccount = (field, input) => {
        switch (field) {
            case 'companyId':
                setCompanyId(input);
                break;
            case 'accountCodeName':
                setAccountCodeName(input);
                break;
            case 'accountCodeId':
                setAccountCodeId(input);
                break;
            default:
                break;
        }
    };

    const checkIfExistingAccountCard = (options, cardId, accountCaseId, edit = false) => {
        const existingAccountCard = (options || []).filter(card =>
            (card.accountCaseId === accountCaseId && accountCaseId) ||
            (card.cardId === cardId && cardId)
        );
        const isExist = existingAccountCard && (edit ? existingAccountCard.length > 1 : existingAccountCard.length > 0);
        if (isExist) {
            toast.info(t('AccountCardExists', { accountCodeId: existingAccountCard[0]?.cardId }));
            return true;
        }
        return false;
    };

    const handleAddAccountCard = async () => {
        if (checkIfExistingAccountCard(accountCardOptions, accountCodeId, companyId)) return;
        try {
            setLoading(true);
            const response = await EnumsService.AddAccountCard(client.caseId, accountCodeId, accountCodeName, companyId, palCode, token);
            if (response.ok) {
                const newCardId = await response.json();
                const selectedAccountCard = { cardId: newCardId, caseId: client.caseId, accountCaseId: companyId, name: accountCodeName, palCode: palCode, client: null };
                setAccountCardOptions(prev => [...(prev || []), selectedAccountCard]);
                setAddEditableItem(prev => prev ? { ...prev, accountCodeId: newCardId?.toString(), accountCodeName: accountCodeName?.toString(), accountCaseId: companyId?.toString() } : prev);
                setSelectedOption(selectedAccountCard);
                toast.success(t('CustomerSupplierAddedSuccessfully'));
            } else {
                const errorText = await response.text();
                toast.error(t('FailedToAddCustomerSupplier') || errorText);
            }
        } catch (err) {
            toast.error(t('FailedToAddCustomerSupplier'));
        } finally {
            setLoading(false);
            setPalCode('');
            setAccountCodeId('');
            setAccountCodeName('');
            setCompanyId('');
            setAddNewAccountCardDocument(false);
        }
    };

    const handleSaveEditAccountCard = async () => {
        if (!oldSelectedAccountCardItem || checkIfExistingAccountCard(accountCardOptions, accountCodeId, companyId, true)) return;
        try {
            setLoading(true);
            const response = await EnumsService.UpdateAccountCard(client.caseId, oldSelectedAccountCardItem.cardId, oldSelectedAccountCardItem.name, accountCodeId, accountCodeName, companyId, palCode, token);
            if (response.ok) {
                const selectedAccountCard = { cardId: accountCodeId, caseId: client.caseId, accountCaseId: companyId, name: accountCodeName, palCode: palCode, client: null };
                setAccountCardOptions(prev => (prev || []).filter(card => card.cardId !== selectedAccountCard.cardId).concat([selectedAccountCard]));
                setAddEditableItem(prev => prev ? { ...prev, accountCodeId: accountCodeId?.toString(), accountCodeName: accountCodeName?.toString(), accountCaseId: companyId?.toString() } : prev);
                setSelectedOption(selectedAccountCard);
                toast.success(t('CustomerSupplierUpdatedSuccessfully'));
            } else {
                toast.error(t('FailedToUpdateCustomerSupplier'));
            }
        } catch (err) {
            toast.error(t('FailedToUpdateCustomerSupplier'));
        } finally {
            setLoading(false);
            setPalCode('');
            setAccountCodeId('');
            setAccountCodeName('');
            setCompanyId('');
            setAddNewAccountCardDocument(false);
            setShowAccountCardInputsEdit(false);
        }
    };

    const handleCloseAddNewAccountCardDocument = () => {
        setAccountCodeId('');
        setAccountCodeName('');
        setCompanyId('');
        setAddNewAccountCardDocument(false);
        setShowAccountCardInputsEdit(false);
    };

    const handleAccountEdit = (option) => {
        setShowAccountCardInputsEdit(true);
        setAccountCodeName(option.name);
        setAccountCodeId(option.cardId);
        setCompanyId(option.caseId || option.accountCaseId);
        setPalCode(option.palCode || '');
        setOldSelectedAccountCardItem({ cardId: option.cardId, caseId: option.caseId, accountCaseId: option.accountCaseId, name: option.name, palCode: option.palCode || '', client: null });
        setAddNewAccountCardDocument(true);
    };

    const handleAccountDelete = (option) => {
        setActionType('deleteAccountCard');
        setIsConfirmationVisible(true);
        setItemIdToDelete({ itemId: option?.cardId, type: 'accountCard' });
    };

    /** Next/prev in full row order (any type); opens EditModal or PA modal per row docType */
    const handlePreviousNav = useCallback(() => {
        if (navigationList.length === 0 || currentNavigationIndex <= 0) return;
        const newIdx = currentNavigationIndex - 1;
        setCurrentNavigationIndex(newIdx);
        const row = navigationList[newIdx];
        internalRowClick(row, sectionKeyForDocType(row.docType));
    }, [navigationList, currentNavigationIndex]);
    const handleNextNav = useCallback(() => {
        if (navigationList.length === 0 || currentNavigationIndex < 0 || currentNavigationIndex >= navigationList.length - 1) return;
        const newIdx = currentNavigationIndex + 1;
        setCurrentNavigationIndex(newIdx);
        const row = navigationList[newIdx];
        internalRowClick(row, sectionKeyForDocType(row.docType));
    }, [navigationList, currentNavigationIndex]);

    const handlePreviousPa = useCallback(() => {
        if (navigationList.length) {
            handlePreviousNav();
            return;
        }
        if (selectedSectionKey !== 'PaymentAcceptances' || editModalList.length === 0 || currentPaIndex <= 0) return;
        const newIdx = currentPaIndex - 1;
        setCurrentPaIndex(newIdx);
        const row = editModalList[newIdx];
        internalRowClick(row, selectedSectionKey);
    }, [navigationList.length, handlePreviousNav, selectedSectionKey, editModalList, currentPaIndex]);
    const handleNextPa = useCallback(() => {
        if (navigationList.length) {
            handleNextNav();
            return;
        }
        if (selectedSectionKey !== 'PaymentAcceptances' || editModalList.length === 0 || currentPaIndex < 0 || currentPaIndex >= editModalList.length - 1) return;
        const newIdx = currentPaIndex + 1;
        setCurrentPaIndex(newIdx);
        const row = editModalList[newIdx];
        internalRowClick(row, selectedSectionKey);
    }, [navigationList.length, handleNextNav, selectedSectionKey, editModalList, currentPaIndex]);

    const handlePreviousEdit = useCallback(() => {
        if (navigationList.length) {
            handlePreviousNav();
            return;
        }
        if (editModalList.length === 0 || currentEditIndex <= 0) return;
        const newIdx = currentEditIndex - 1;
        setCurrentEditIndex(newIdx);
        const row = editModalList[newIdx];
        internalRowClick(row, selectedSectionKey);
    }, [navigationList.length, handlePreviousNav, editModalList, currentEditIndex, selectedSectionKey]);
    const handleNextEdit = useCallback(() => {
        if (navigationList.length) {
            handleNextNav();
            return;
        }
        if (editModalList.length === 0 || currentEditIndex < 0 || currentEditIndex >= editModalList.length - 1) return;
        const newIdx = currentEditIndex + 1;
        setCurrentEditIndex(newIdx);
        const row = editModalList[newIdx];
        internalRowClick(row, selectedSectionKey);
    }, [navigationList.length, handleNextNav, editModalList, currentEditIndex, selectedSectionKey]);

    /* ------------------------------------------------------------------- */
    const closeEditModal = () => { 
        setEditModalOpen(false); 
        setEditedItem(null); 
        setCurrentNavigationIndex(-1);
        // Reset the state variables when closing the edit modal
        if (setEditModalDataSource) setEditModalDataSource(null);
        if (setSelectedDocumentId) setSelectedDocumentId(null);
        if (setSelectedSectionKey) setSelectedSectionKey(null);
        if (setIsJustEditModalOpen && openJustEditModal) {
            // Only close parent modal if it was opened directly for editing
            setIsJustEditModalOpen(false);
            onClose();
        }
    };
    const closePaModal = () => { 
        setPaModalOpen(false); 
        setAddEditableItem(null); 
        setCurrentPaIndex(-1);
        setCurrentNavigationIndex(-1);
        setSelectedOption(null);
        setAccountCodeId('');
        setAccountCodeName('');
        setCompanyId('');
        setPalCode('');
        setShowAccountCardInputsEdit(false);
        setAddNewAccountCardDocument(false);
        if (setEditModalDataSource) setEditModalDataSource(null);
        if (setSelectedDocumentId) setSelectedDocumentId(null);
        if (setSelectedSectionKey) setSelectedSectionKey(null);
        if (setIsJustEditModalOpen && openJustEditModal) {
            setIsJustEditModalOpen(false);
            onClose();
        }
    };

    /** Resolve isUnprocessed: use row flag if provided, otherwise fall back to client.documentIds */
    const resolveIsUnprocessed = (row, sectionKey) => {
        if (row.isUnprocessed !== undefined) return row.isUnprocessed;
        if (!client?.documentIds) return false;
        const ids =
            sectionKey === 'Incomes' ? client.documentIds.incomes :
            sectionKey === 'Expenses' ? client.documentIds.expenses :
            sectionKey === 'JournalEntries' ? client.documentIds.journalEntries :
            sectionKey === 'PaymentAcceptances' ? client.documentIds.checks :
            null;
        return ids?.includes(row.id) ?? false;
    };

    /** REAL dispatcher **/
    const internalRowClick = async (row, sectionKey) => {
        if (onSelectDocument) {
            onSelectDocument(row, sectionKey);
            return;
        }

        try {
            const resp = await DocumentService.GetDocumentById(sectionKey, caseId, row.id, token);
            
            if (!resp.ok) throw new Error(await resp.text());
            const json = await resp.json();
            json.documentType = sectionKey;
            // The section key is the authoritative type here: a stale `docType` coming back from
            // the API would send document transfer to the wrong entity.
            json.docType = sectionKey;
            const isUnprocessed = resolveIsUnprocessed(row, sectionKey);
            json.isUnprocessed = isUnprocessed;

            if (sectionKey === "PaymentAcceptances") {
                setEditModalOpen(false);
                // GetById can return null child collections; keep them as [] so Update
                // matches PaymentAcceptance.jsx and never sends Offsets:null.
                setAddEditableItem({
                    ...json,
                    checks: json.checks ?? [],
                    creditCards: json.creditCards ?? [],
                    cash: json.cash ?? [],
                    bankTransactions: json.bankTransactions ?? [],
                    offsets: json.offsets ?? [],
                });
                const matchingAccountCard = accountCardOptions.find(card => card.cardId?.toString() === json.accountCodeId?.toString());
                if (matchingAccountCard) setSelectedOption(matchingAccountCard);
                else setSelectedOption(null);
                setPaModalOpen(true);
                return;
            } else {
                setPaModalOpen(false);
                setStatusCheck(!isUnprocessed);
                setEditedItem(normalizeItem(json));
                setEditModalOpen(true);
            }




        } catch (err) {
            console.error("fetch-doc failed:", err);
        }
    };

    /* fast-escape */
    if (!open) return null;

    /* ──────────────────────────────────────────────────────────────── */
    return (
        <>
            {/* ===================== MAIN SEARCH MODAL ===================== */}
            <Dialog open={open && !openJustEditModal  } sx={{ zIndex: resolvedDialogZIndex }} onClose={onClose} PaperComponent={PaperDraggable}
                fullWidth maxWidth="xl" hideBackdrop disableEnforceFocus disableAutoFocus PaperProps={{ className: `dialog-paper ${direction}`, sx: { maxHeight: "90vh" } }} dir={direction}>

                <DialogTitle id="dialog-draggable-title" className="dialog-title">
                    <Typography variant="h6" className="dialog-title-text">{t("TransactionSearch")}</Typography>
                    <IconButton onClick={onClose} size="small" className="dialog-title-close">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                {/* ───────────── filter bar ───────────── */}
                <DialogContent dividers className="dialog-content">
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
                        <Box className="txn-search-filters" dir={direction}>
                            <div className="txn-search-filter-selector">
                                <AutocompleteSelectFilter
                                    options={filterSelectOptions}
                                    selectedValues={filterSelectOptions.filter((o) => activeFilterKeys.includes(o.value))}
                                    onChange={handleActiveFiltersChange}
                                    label={t("SelectFilters") || "Select filters"}
                                    placeholder={t("AddFilter") || "Add filter…"}
                                    isMultiple
                                    direction={direction}
                                    portalMenu
                                    portalZIndex={overlayPopperZIndex}
                                    searchable
                                    showAllSelected
                                />
                            </div>

                            <Box className="txn-search-filter-grid">
                                {isFilterActive("documentDate") && (
                                    <FilterGroup compact label={t("DocumentDate") || t("DocumentDateFrom")}>
                                        <DatePicker
                                            label={t("DocumentDateFrom") || t("FromDate")}
                                            views={['year', 'month']}
                                            openTo="month"
                                            format="MM/YYYY"
                                            value={filters.fromDate}
                                            onChange={(v) => setFilters({ ...filters, fromDate: v })}
                                            slotProps={withDatePickerSlotProps({
                                                    size: "small", fullWidth: true,
                                                    InputProps: { className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` },
                                                })}
                                        />
                                        <DatePicker
                                            label={t("DocumentDateTo") || t("ToDate")}
                                            views={['year', 'month']}
                                            openTo="month"
                                            format="MM/YYYY"
                                            value={filters.toDate}
                                            onChange={(v) => setFilters({ ...filters, toDate: v })}
                                            slotProps={withDatePickerSlotProps({
                                                    size: "small", fullWidth: true,
                                                    InputProps: { className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` },
                                                })}
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("uploadDate") && (
                                    <FilterGroup compact label={t("UploadDate") || t("UploadDateFrom")}>
                                        <DatePicker
                                            label={t("UploadDateFrom")}
                                            value={filters.uploadDateFrom ? dayjs.utc(filters.uploadDateFrom) : null}
                                            onChange={(v) => {
                                                if (v && v.isValid && v.isValid()) {
                                                    const utcMidnight = new Date(Date.UTC(v.year(), v.month(), v.date(), 0, 0, 0, 0));
                                                    setFilters({ ...filters, uploadDateFrom: utcMidnight });
                                                } else {
                                                    setFilters({ ...filters, uploadDateFrom: null });
                                                }
                                            }}
                                            slotProps={withDatePickerSlotProps({
                                                    size: "small", fullWidth: true,
                                                    InputProps: { className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` },
                                                })}
                                        />
                                        <DatePicker
                                            label={t("UploadDateTo")}
                                            value={filters.uploadDateTo ? dayjs.utc(filters.uploadDateTo) : null}
                                            onChange={(v) => {
                                                if (v && v.isValid && v.isValid()) {
                                                    const utcMidnight = new Date(Date.UTC(v.year(), v.month(), v.date(), 0, 0, 0, 0));
                                                    setFilters({ ...filters, uploadDateTo: utcMidnight });
                                                } else {
                                                    setFilters({ ...filters, uploadDateTo: null });
                                                }
                                            }}
                                            slotProps={withDatePickerSlotProps({
                                                    size: "small", fullWidth: true,
                                                    InputProps: { className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` },
                                                })}
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("reportingMonth") && (
                                    <FilterGroup compact label={t("ReportingMonth") || t("ReportingMonthFrom")}>
                                        <DatePicker
                                            label={t("ReportingMonthFrom")}
                                            views={['year', 'month']}
                                            openTo="month"
                                            format="MM/YYYY"
                                            value={filters.reportingMonthFrom}
                                            onChange={(v) => setFilters({ ...filters, reportingMonthFrom: v })}
                                            slotProps={withDatePickerSlotProps({
                                                    size: "small", fullWidth: true,
                                                    InputProps: { className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` },
                                                })}
                                        />
                                        <DatePicker
                                            label={t("ReportingMonthTo")}
                                            views={['year', 'month']}
                                            openTo="month"
                                            format="MM/YYYY"
                                            value={filters.reportingMonthTo}
                                            onChange={(v) => setFilters({ ...filters, reportingMonthTo: v })}
                                            slotProps={withDatePickerSlotProps({
                                                    size: "small", fullWidth: true,
                                                    InputProps: { className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` },
                                                })}
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("amount") && (
                                    <FilterGroup label={t("Amount")}>
                                        <TextField
                                            label={t("MinAmount")}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={filters.minAmount}
                                            onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                                            InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                        />
                                        <TextField
                                            label={t("MaxAmount")}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={filters.maxAmount}
                                            onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                                            InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("sortCode") && (
                                    <FilterGroup label={t("SortCode")}>
                                        <AutocompleteSelectFilter
                                            options={sortCodeOpts}
                                            selectedValues={sortCodeOpts.find((o) => o.value === filters.sortCodeId) || null}
                                            onChange={(_, nv) => setFilters({ ...filters, sortCodeId: nv ? nv.value : null })}
                                            placeholder={t("SortCode")}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            portalZIndex={overlayPopperZIndex}
                                            searchable
                                            clearable
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("debitAccount") && (
                                    <FilterGroup label={t("Debit Account")}>
                                        <AutocompleteSelectFilter
                                            options={debitCreditOpts}
                                            selectedValues={debitCreditOpts.find((o) => o.value === filters.debitAccount) || null}
                                            onChange={(_, nv) => setFilters({ ...filters, debitAccount: nv ? nv.value : null })}
                                            placeholder={t("Debit Account")}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            portalZIndex={overlayPopperZIndex}
                                            searchable
                                            clearable
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("creditAccount") && (
                                    <FilterGroup label={t("Credit Account")}>
                                        <AutocompleteSelectFilter
                                            options={debitCreditOpts}
                                            selectedValues={debitCreditOpts.find((o) => o.value === filters.creditAccount) || null}
                                            onChange={(_, nv) => setFilters({ ...filters, creditAccount: nv ? nv.value : null })}
                                            placeholder={t("Credit Account")}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            portalZIndex={overlayPopperZIndex}
                                            searchable
                                            clearable
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("description") && (
                                    <FilterGroup label={t("Description")}>
                                        <TextField
                                            label={t("Description") + "…"}
                                            size="small"
                                            fullWidth
                                            value={filters.descriptionContains}
                                            onChange={(e) => setFilters({ ...filters, descriptionContains: e.target.value })}
                                            InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("reference") && (
                                    <FilterGroup wide label={t("Reference")}>
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={filters.referenceMode || "exact"}
                                            onChange={handleReferenceModeChange}
                                            className="txn-search-ref-mode"
                                            aria-label={t("ReferenceMode") || "Reference mode"}
                                            fullWidth
                                        >
                                            {referenceModeOptions.map((opt) => (
                                                <ToggleButton key={opt.value} value={opt.value} sx={{ flex: 1 }}>
                                                    {opt.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        {(filters.referenceMode || "exact") === "range" ? (
                                            <Box className="txn-search-filter-group-fields" sx={{ width: "100%" }}>
                                                <TextField
                                                    label={t("ReferenceFrom") || "Reference From"}
                                                    size="small"
                                                    fullWidth
                                                    value={filters.referenceFrom}
                                                    onChange={(e) => setFilters({ ...filters, referenceFrom: e.target.value })}
                                                    InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                                />
                                                <TextField
                                                    label={t("ReferenceTo") || "Reference To"}
                                                    size="small"
                                                    fullWidth
                                                    value={filters.referenceTo}
                                                    onChange={(e) => setFilters({ ...filters, referenceTo: e.target.value })}
                                                    InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                                />
                                            </Box>
                                        ) : (
                                            <TextField
                                                label={
                                                    (filters.referenceMode === "include"
                                                        ? (t("ReferenceInclude") || t("FilterContains") || "Contains")
                                                        : (t("ReferenceExact") || "Exact"))
                                                    + " — " + (t("Reference") || "Reference")
                                                }
                                                size="small"
                                                fullWidth
                                                value={filters.referenceContains}
                                                onChange={(e) => setFilters({ ...filters, referenceContains: e.target.value })}
                                                InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                            />
                                        )}
                                    </FilterGroup>
                                )}

                                {isFilterActive("reference2") && (
                                    <FilterGroup label={t("Reference2")}>
                                        <TextField
                                            label={t("Reference2") + "…"}
                                            size="small"
                                            fullWidth
                                            value={filters.reference2Contains}
                                            onChange={(e) => setFilters({ ...filters, reference2Contains: e.target.value })}
                                            InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("accountCardRange") && (
                                    <FilterGroup wide label={t("AccountCardRange") || "Account Card Range"}>
                                        <AutocompleteSelectFilter
                                            options={debitCreditOpts}
                                            selectedValues={debitCreditOpts.find((o) => o.value === filters.accountCardFrom) || null}
                                            onChange={(_, nv) => setFilters({ ...filters, accountCardFrom: nv ? nv.value : "" })}
                                            placeholder={t("AccountCardFrom") || "From Account Card"}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            portalZIndex={overlayPopperZIndex}
                                            searchable
                                            clearable
                                        />
                                        <AutocompleteSelectFilter
                                            options={debitCreditOpts}
                                            selectedValues={debitCreditOpts.find((o) => o.value === filters.accountCardTo) || null}
                                            onChange={(_, nv) => setFilters({ ...filters, accountCardTo: nv ? nv.value : "" })}
                                            placeholder={t("AccountCardTo") || "To Account Card"}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            portalZIndex={overlayPopperZIndex}
                                            searchable
                                            clearable
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("confirmationNumber") && (
                                    <FilterGroup label={t("ConfirmationNumber")}>
                                        <TextField
                                            label={t("ConfirmationNumber") + "…"}
                                            size="small"
                                            fullWidth
                                            value={filters.confirmationNumber}
                                            onChange={(e) => setFilters({ ...filters, confirmationNumber: e.target.value })}
                                            InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("uploadNumber") && (
                                    <FilterGroup label={t("UploadNumber") || t("UploadNumberFrom")}>
                                        <TextField
                                            label={t("UploadNumberFrom")}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={filters.uploadNumberFrom}
                                            onChange={(e) => setFilters({ ...filters, uploadNumberFrom: e.target.value })}
                                            InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                        />
                                        <TextField
                                            label={t("UploadNumberTo")}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={filters.uploadNumberTo}
                                            onChange={(e) => setFilters({ ...filters, uploadNumberTo: e.target.value })}
                                            InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                        />
                                    </FilterGroup>
                                )}

                                {isFilterActive("accountCaseId") && (
                                    <FilterGroup label={t("AccountCaseId")}>
                                        <TextField
                                            label={t("AccountCaseId") + "…"}
                                            size="small"
                                            fullWidth
                                            value={filters.accountCaseId}
                                            onChange={(e) => setFilters({ ...filters, accountCaseId: e.target.value })}
                                            InputProps={{ className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}` }}
                                        />
                                    </FilterGroup>
                                )}
                            </Box>

                            <Box className="txn-search-actions">
                                <Button
                                    variant="contained"
                                    size="medium"
                                    disabled={loading}
                                    startIcon={<SearchIcon fontSize="small" />}
                                    onClick={fetchData}
                                    className="txn-search-primary-btn"
                                >
                                    {t("Search")}
                                </Button>
                            </Box>
                        </Box>
                    </LocalizationProvider>



                    <Divider className="txn-search-divider" />

                    {/* ───────────── result accordions ───────────── */}
                    {loading ? (
                        <Typography align="center" className="txn-search-loading">{t("Loading")}…</Typography>
                    ) : (
                        <div className="txn-search-results">
                            {sections.map(({ key, title, data }) => (
                                <Accordion
                                    key={`${key}-${data.length}`}
                                    defaultExpanded={data.length > 0}
                                    disableGutters
                                    elevation={0}
                                    square
                                    className="txn-search-accordion"
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography fontWeight="bold">{title} ({data.length})</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TxnTable rows={data} onRowClick={r => internalRowClick(r, key)} />
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <CustomDeleteConfirmationModal
                isVisible={isConfirmationVisible}
                onClose={() => setIsConfirmationVisible(false)}
                onDelete={handleConfirm}
                confirmMessage={getConfirmMessage()}
                titleMessage={getTitleMessage()}
                type={actionType}
            />

            {/* ===================== EditModal (Income/Expense/Journal) ===================== */}
            {editModalOpen && editedItem && (
                <EditModal
                    open={editModalOpen}
                    handleClose={closeEditModal}
                    editedItem={editedItem}
                    fade={fade}
                    subAccountantOptions={subAccountantOptions}
                    handlePrevious={navigationList.length ? handlePreviousNav : handlePreviousEdit}
                    handleNext={navigationList.length ? handleNextNav : handleNextEdit}
                    currentIndex={navigationList.length ? currentNavigationIndex : (editModalList.length ? currentEditIndex : -1)}
                    filteredAndSortedData={navigationList.length ? navigationList : editModalList}
                    NoDocumentsPdf={NoDocumentsPdf}
                    t={t}
                    handleAccountChange={handleAccountChange}
                    isRange={isRange}
                    isClosedReportingMonth={editedItem.isClosedReportingMonth}
                    accountCardOptions={accountCardOptionsWithAdd}
                    handleChange={handleChange}
                    handleDateChange={handleDateChange}
                    setReportingMonthDate={setReportingMonthDate}
                    handleVatRadioChange={handleVatRadioChange}
                    statusCheck={statusCheck}
                    handleRadioChange={handleRadioChange}
                    type={editedItem.documentType}
                    editedAddAccountCard={editedAddAccountCard}
                    handleCloseEditAddNewAccountCardDocument={handleCloseEditAddNewAccountCardDocument}
                    handleEditAddAccountChange={handleEditAddAccountChange}
                    sortCodeIdOptions={sortCodeIdOptions}
                    debitAccountOptions={debitAccountOptions}
                    creditAccountOptions={creditAccountOptions}
                    handleUpdate={handleUpdate}
                    handleCancel={handleCancel}
                    handleDeleteClick={handleEditDeleteClick}
                    client={client}
                    token={token}
                    setAccountCardOptions={setAccountCardOptions}
                    setLoading={setLoading}
                    setEditedAddAccountCard={setEditedAddAccountCard}
                    setEditedItem={setEditedItem}
                    handleReopenEdit={handleReopenEdit}
                    palCodes={palCodes}
                    RefreshPage={RefreshPage}
                    handleAccountCardSave={handleAccountCardSave}
                    fetchEnums={fetchEnums}
                    assets={assets}
                    assetClicked={handleAssetClicked}
                    hideStatus={hideStatusBadge}
                />
            )}

            {/* ===================== Payment Acceptance Create/Edit Modal (global) ===================== */}
            {paModalOpen && addEditableItem && (
                <PaymentAcceptanceCreateEditModal
                    openInvoiceCreatorModal={true}
                    handleClose={closePaModal}
                    t={t}
                    itemEditing={!!addEditableItem?.id}
                    addEditableItem={addEditableItem}
                    setPaymentDate={setPaymentDate}
                    setReportingMonthDate={setReportingMonthDate}
                    handleChangeAccountType={handleChangeAccountType}
                    handleDescriptionChange={handleDescriptionChange}
                    handleChangeOriginalTaxClearance={handleChangeOriginalTaxClearance}
                    handleAddEditableItemChange={handleAddEditableItemChange}
                    handleRadioChange={handlePaymentAcceptanceRadioChange}
                    handleAddItem={handleAddItem}
                    handleItemChange={handleItemChange}
                    handleRemoveItem={handleRemoveItem}
                    accountCardOptions={accountCardOptions}
                    handleSave={handleSavePaymentAcceptance}
                    handleClickDoneCreation={closePaModal}
                    buttonDisabled={buttonDisabled}
                    isClosedReportingMonthAndIsRange={false}
                    isClosedReportingMonth={false}
                    isRange={false}
                    paymentBeingEdited={paymentBeingEdited}
                    setAccount={setAccount}
                    showAccountCardInputsEdit={showAccountCardInputsEdit}
                    handleSaveEditAccountCard={handleSaveEditAccountCard}
                    handleAddAccountCard={handleAddAccountCard}
                    handleCloseAddNewAccountCardDocument={handleCloseAddNewAccountCardDocument}
                    handleAccountEdit={handleAccountEdit}
                    handleAccountDelete={handleAccountDelete}
                    handleMuiChange={handleMuiChange}
                    addNewAccountCardDocument={addNewAccountCardDocument}
                    companyId={companyId}
                    accountCodeName={accountCodeName}
                    accountCodeId={accountCodeId}
                    palCode={palCode}
                    setPalCode={setPalCode}
                    customStyles={{}}
                    client={client}
                    setLoading={setLoading}
                    handleTransferRefresh={() => {}}
                    token={token}
                    statusCheck={statusCheck}
                    selectedOption={selectedOption}
                    type={addEditableItem?.paymentAcceptanceType ?? ''}
                    direction={direction}
                    setAddNewAccountCardDocument={setAddNewAccountCardDocument}
                    CustomPalCode={CustomPalCode}
                    handlePrevious={navigationList.length ? handlePreviousNav : handlePreviousPa}
                    handleNext={navigationList.length ? handleNextNav : handleNextPa}
                    currentIndex={navigationList.length ? currentNavigationIndex : (selectedSectionKey === 'PaymentAcceptances' ? currentPaIndex : -1)}
                    filteredAndSortedData={navigationList.length ? navigationList : (selectedSectionKey === 'PaymentAcceptances' ? editModalList : [])}
                    showType={{ type: 'all', index: -1 }}
                    handleDeleteClick={handlePaDeleteClick}
                    fetchEnums={fetchEnums}
                    sortDate={addEditableItem?.date}
                    subAccountantOptions={subAccountantOptions}
                    hideStatus={hideStatusBadge}
                    showForceProcess={true}
                />
            )}

        </>
    );
};

TransactionSearchResultModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    caseId: PropTypes.string.isRequired,
    accountantId: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    accountsData: PropTypes.array,
    sortCodes: PropTypes.array,
    onRowClick: PropTypes.func,
    onSelectDocument: PropTypes.func,
    dialogZIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    editModalUpdated: PropTypes.bool,
    setEditModalUpdated: PropTypes.func,
};

export default TransactionSearchResultModal;
